import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import useDropdownMenu from 'react-accessible-dropdown-menu-hook';

import { rem } from '../utilities/style';
import { colors } from '../theme/colors';
import { fontWeights } from '../theme/type';

import { ReactComponent as IconSelectArrowDown } from '../images/icons/select-arrow-down.svg';
import stacking from '../theme/stacking';
import TextRenderer from './TextRenderer';

const Container = styled.div`
  position: relative;
  display: inline-block;
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  border: 1px solid ${colors.gray['200']};
  background: ${colors.white};
  border-radius: ${rem(50)};
  font-weight: ${fontWeights.semiBold};
  color: ${colors.blue['800']};
  padding: ${rem(12)} ${rem(18)};
  cursor: pointer;

  &[aria-expanded=true] {
    border-color: ${colors.blue['500']};
  }

  ${({ hasValue }) => hasValue && `
    border-color: ${colors.blue['500']};
  `}

  ${({ small }) => small && `
    padding: ${rem(10)} ${rem(16)};
  `}


  ${({ fullWidth }) => fullWidth && `
    width: 100%;
  `}
`;

const ToggleText = styled.span`
  display: inline-block;
  max-width: ${rem(140)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ small }) => small && `
  max-width: ${rem(154)};
  font-size: ${rem(14)};
  `}

  ${({ fullWidth }) => fullWidth && `
    width: 100%;
    text-align: left;
    max-width: none;
  `}
`;

const ToggleIcon = styled(IconSelectArrowDown)`
  margin-left: ${rem(8)};

  ${({ open }) => open && `
    transform: rotate(180deg);
  `}
`;

const Panel = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  margin-top: 4px;
  background: ${colors.white};
  display: none;
  border: 1px solid ${colors.gray['200']};
  border-radius: ${rem(10)};
  box-shadow: 0px 30px 36px rgba(29, 37, 45, .15);
  padding: ${rem(10)} ${rem(18)};
  z-index: ${stacking.select};
  line-height: 1.2;
  max-width: ${rem(250)};
  min-width: max-content;

  ${({ open }) => open && `
    display: block;
  `}

  ${({ fullWidth }) => fullWidth && `
    max-width: none;
    min-width: 0;
  `}
`;

const Items = styled.div`
  padding: 5px;
`;

const ItemButton = styled.button`
  display: block;
  border: none;
  background: none;
  padding: ${rem(6)} 0;
  color: ${colors.gray['600']};
  text-decoration: none;
  text-align: left;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &[aria-current=page] {
    color: ${colors.blue['500']};
    font-weight: ${fontWeights.semiBold};
    text-decoration: none;
  }
`;

const Select = ({
  label,
  options,
  defaultValue = '',
  multiple = false,
  onChange,
  className,
  small = false,
  fullWidth = false
}) => {
  const formattedDefault = defaultValue.length > 0 || typeof defaultValue === 'number' ? defaultValue : '';
  const initialValue = Array.isArray(formattedDefault) ? formattedDefault : [formattedDefault];
  const [value, setValue] = useState(initialValue);
  const activeOption = !multiple ? options.find((option) => option.value === value[0]) : null;
  const {
    buttonProps,
    itemProps,
    isOpen,
    setIsOpen
  } = useDropdownMenu(options.length);

  const handleChange = (itemValue) => {
    let newValue = [itemValue];

    if (multiple) {
      const index = value.indexOf(itemValue);

      if (index === -1) {
        newValue = [...value, itemValue];
      } else {
        newValue = [...value.slice(0, index), ...value.slice(index + 1)];
      }
    }

    setValue(newValue);
    setIsOpen(false);

    if (typeof onChange === 'function') {
      onChange(multiple ? newValue : itemValue);
    }
  };

  useEffect(() => {
    const newFormattedDefault = defaultValue.length > 0 || typeof defaultValue === 'number' ? defaultValue : '';
    const newValue = Array.isArray(newFormattedDefault)
      ? newFormattedDefault : [newFormattedDefault];

    setValue(newValue);
  }, [defaultValue]);

  return (
    <Container className={className}>
      <ToggleButton
        type="button"
        hasValue={value.length > 0 && value[0] !== ''}
        onKeyDown={buttonProps.onKeyDown}
        onClick={buttonProps.onClick}
        tabIndex={buttonProps.tabIndex}
        ref={buttonProps.ref}
        aria-haspopup={buttonProps['aria-haspopup']}
        aria-expanded={buttonProps['aria-expanded']}
        small={small}
        fullWidth={fullWidth}
      >
        <ToggleText small={small} fullWidth={fullWidth}>
          <TextRenderer text={activeOption ? activeOption.label : label} />
        </ToggleText>
        <ToggleIcon open={isOpen} />
      </ToggleButton>
      <Panel open={isOpen} role="menu" fullWidth={fullWidth}>
        <Items>
          {options.map((option, index) => (
            <ItemButton
              type="button"
              key={option.value}
              role="menuitem"
              onClick={() => handleChange(option.value)}
              aria-current={value.indexOf(option.value) !== -1 ? 'page' : null}
              onKeyDown={itemProps[index].onKeyDown}
              tabIndex={itemProps[index].tabIndex}
              ref={itemProps[index].ref}
            >
              <TextRenderer text={option.label} />
            </ItemButton>
          ))}
        </Items>
      </Panel>
    </Container>
  );
};

Select.propTypes = {
  label: PropTypes.string,
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.number)
  ]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
      ]).isRequired,
      label: PropTypes.string.isRequired
    })
  ),
  multiple: PropTypes.bool,
  onChange: PropTypes.func,
  className: PropTypes.string,
  small: PropTypes.bool,
  fullWidth: PropTypes.bool
};

// Convert to styled component so we can reference it in other component styles
export default styled(Select)();
